import { useEffect, useRef, useState } from 'react';
import { useOnboarding } from '@/contexts/OnboardingContext';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

export function TourOverlay() {
  const { activeTour, currentStepData, skipTour } = useOnboarding();
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const measureTarget = () => {
    if (!currentStepData?.targetSelector) {
      setSpotlight(null);
      return;
    }
    const el = document.querySelector(currentStepData.targetSelector);
    if (!el) {
      setSpotlight(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setSpotlight({
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    });
    // Scroll element into view if off-screen
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    if (!activeTour) {
      setSpotlight(null);
      return;
    }

    // Delay to let route/DOM settle
    if (measureTimerRef.current) clearTimeout(measureTimerRef.current);
    measureTimerRef.current = setTimeout(measureTarget, 350);

    // Set up ResizeObserver to re-measure on layout changes
    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(() => {
      if (measureTimerRef.current) clearTimeout(measureTimerRef.current);
      measureTimerRef.current = setTimeout(measureTarget, 100);
    });
    observerRef.current.observe(document.body);

    return () => {
      observerRef.current?.disconnect();
      if (measureTimerRef.current) clearTimeout(measureTimerRef.current);
    };
  }, [activeTour, currentStepData?.targetSelector]);

  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [skipTour]);

  if (!activeTour) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        pointerEvents: 'none',
      }}
    >
      {spotlight ? (
        /* Spotlight hole using box-shadow trick */
        <div
          style={{
            position: 'absolute',
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
            transition: 'top 0.3s ease-out, left 0.3s ease-out, width 0.3s ease-out, height 0.3s ease-out',
            outline: '2px solid hsl(var(--primary))',
            outlineOffset: 2,
          }}
        />
      ) : (
        /* No target – full dim overlay */
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
          }}
        />
      )}
    </div>
  );
}
